'use client'

import type { ReactNode } from 'react'
import { Globe, Menu } from 'lucide-react'
import { IconButton } from '@/components/platform/ui'
import { InstrumentLabel } from '../ui'

/**
 * The single top bar.
 *
 * Replaces the previous two-bar arrangement (a global nav plus a separate
 * "compact summary" strip inside the content column) — they carried
 * overlapping information and cost 90px of vertical space before any result
 * was visible.
 */
export function DashboardTopBar({
  activeDomain,
  brand,
  title,
  statusLine,
  actions,
  onOpenNav,
}: {
  activeDomain?: string | null
  brand?: string
  /** Overrides the domain/brand pair — used by the new-audit view. */
  title?: string
  /** e.g. "13/13 tools · last audit 1d ago". */
  statusLine?: ReactNode
  /** Run / save / export / share controls. */
  actions?: ReactNode
  onOpenNav: () => void
}) {
  return (
    // 72px from the radar-v2 arrangement, applied as a min-height at `sm` and
    // up rather than a fixed height: below that the bar deliberately wraps its
    // actions onto a second line, and a hard height would clip them.
    <header className='flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline bg-surface-app px-4 py-3 sm:min-h-[72px] sm:px-8'>
      <IconButton
        icon={Menu}
        aria-label='Open navigation'
        variant='ghost'
        size='sm'
        onClick={onOpenNav}
        className='lg:hidden'
      />

      {title ? (
        <span className='text-[15px] font-semibold leading-none'>{title}</span>
      ) : activeDomain ? (
        <>
          <span className='flex min-w-0 items-center gap-2'>
            <Globe
              size={16}
              aria-hidden='true'
              className='shrink-0 text-muted-foreground'
            />
            <span className='truncate text-[15px] font-semibold leading-none'>
              {activeDomain}
            </span>
          </span>
          {brand && (
            <InstrumentLabel tone='muted' className='hidden sm:flex'>
              {brand}
            </InstrumentLabel>
          )}
        </>
      ) : (
        <span className='text-[15px] font-semibold leading-none'>
          Radar dashboard
        </span>
      )}

      <div className='flex-1' />

      {statusLine && (
        <span className='hidden font-mono text-xs text-muted-foreground md:inline'>
          {statusLine}
        </span>
      )}
      {actions && <div className='flex items-center gap-2'>{actions}</div>}
    </header>
  )
}
